/**
 * Owner Data Processing Utilities
 * Pure functions for owner-related data transformations
 */
import { normalizeText } from './dataUtils';

/**
 * Filter owners by search term (name, email, or phone)
 * @param {Array} owners - Array of owners to filter
 * @param {string} searchTerm - Search term
 * @returns {Array} Filtered owners array
 */
export function filterOwnersBySearch(owners, searchTerm) {
    if (!searchTerm) return owners;

    const lowerSearch = normalizeText(searchTerm);
    return owners.filter(owner =>
        normalizeText(owner.name).includes(lowerSearch) ||
        normalizeText(owner.email).includes(lowerSearch) ||
        normalizeText(owner.phone).includes(lowerSearch) ||
        normalizeText(owner.address).includes(lowerSearch)
    );
}

/**
 * Transform owner form data for API submission
 * @param {Object} formData - Form data
 * @returns {Object} Transformed data for API
 */
export function transformOwnerFormData({ name, email, phone, address }) {
    return {
        name: name.trim(),
        email: email.trim().toLowerCase(),
        phone: phone.trim(),
        address: address ? address.trim() : ''
    };
}

/**
 * Validate owner form data
 * @param {Object} formData - Form data to validate
 * @returns {Object} Validation result with isValid and errors
 */
export function validateOwnerForm({ name, email, phone, address }) {
    const errors = {};
    
    // Name validation
    if (!name || name.trim().length < 2) {
        errors.name = 'Name must be at least 2 characters long';
    }
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || !emailRegex.test(email.trim())) {
        errors.email = 'Please enter a valid email address';
    }
    
    // Phone validation
    const phoneRegex = /^\+?[\d\s\-\(\)]+$/;
    if (!phone || !phoneRegex.test(phone.trim())) {
        errors.phone = 'Please enter a valid phone number';
    }
    
    // Address validation (required)
    if (!address || address.trim().length === 0) {
        errors.address = 'Address is required';
    } else if (address.trim().length > 60) {
        errors.address = 'Address must be 60 characters or less';
    }
    
    return {
        isValid: Object.keys(errors).length === 0,
        errors
    };
}

/**
 * Sort owners by different criteria
 * @param {Array} owners - Array of owners to sort
 * @param {string} sortBy - Sort criteria ('name', 'email', 'phone')
 * @param {string} sortDirection - Sort direction ('asc' or 'desc')
 * @returns {Array} Sorted owners array
 */
export function sortOwners(owners, sortBy = 'name', sortDirection = 'asc') {
    const sorted = [...owners].sort((a, b) => {
        const aValue = a[sortBy]?.toLowerCase() || '';
        const bValue = b[sortBy]?.toLowerCase() || '';
        
        if (sortDirection === 'asc') {
            return aValue.localeCompare(bValue);
        } else {
            return bValue.localeCompare(aValue);
        }
    });
    
    return sorted;
}

